import type { MouseEventHandler } from 'react';
import { memo, useId } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import type { RIconComponent } from '../remix-icon';
import RIcon from '../remix-icon';
import { TruncatedText } from '../truncated-text';
import Typography from '../typography/typography';

export interface IconTileProps {
  label: string;
  description?: string;
  icon?: RIconComponent;
  isSelected: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  testId?: string;
  disabled?: boolean;
  role?: 'radio' | 'button';
}

const IconTile = ({
  label,
  description,
  isSelected,
  onClick,
  icon,
  testId,
  disabled,
  role = 'button',
}: IconTileProps) => {
  const theme = useTheme();

  const labelId = useId();
  const descriptionId = useId();

  const textAndIconColor = disabled && !isSelected ? 70 : 100;

  return (
    <Wrapper
      $borderColor={
        isSelected ? theme.palette.neutral[100] : theme.palette.neutral[60]
      }
      data-testid={testId}
      onClick={onClick}
      isSelected={isSelected}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      disabled={disabled}
      role={role}
      aria-selected={role === 'radio' ? isSelected : undefined}
    >
      {icon && (
        <RIcon
          icon={icon}
          size="md"
          color={theme.palette.neutral[textAndIconColor]}
        />
      )}
      <TextContainer>
        <Typography
          id={labelId}
          variant={TypographyVariant.bodySm2}
          color={theme.palette.neutral[textAndIconColor]}
        >
          {label}
        </Typography>
        {description && (
          <TruncatedText
            id={descriptionId}
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[textAndIconColor]}
            lineClamp={2}
          >
            {description}
          </TruncatedText>
        )}
      </TextContainer>
    </Wrapper>
  );
};

export default memo(IconTile);

const Wrapper = styled.button`
  ${({
    theme,
    $borderColor,
    isSelected,
  }: {
    theme: ITheme;
    $borderColor: string;
    isSelected: boolean;
  }) => css`
    padding: ${theme.spacing(2.5, 1)};
    border-radius: ${theme.borderRadius.sm};
    border: 1.5px solid ${$borderColor};
    background: ${theme.palette.neutral['00']};
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(2)};

    &:hover:not(:disabled) {
      cursor: ${isSelected ? 'auto' : 'pointer'};
      border: ${isSelected
        ? `1.5px solid ${$borderColor}`
        : `1.5px solid ${theme.palette.neutral[70]}`};
    }

    &:focus {
      border: 1.5px solid ${theme.palette.neutral[100]};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `}
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    text-align: center;
  `}
`;
